import { addDays, startOfDay } from "@app/core";
import { startTransition, useEffect, useState } from "react";

const DAY_MS = 86_400_000;
/** Keep at least this many buffer columns between the visible window and the
 *  rendered edge before recentering. */
const GUARD = 2;

/**
 * Keeps a WIDE, stable set of day-columns mounted around the visible window so
 * day-by-day navigation only translates the track (no re-expansion / re-render
 * per step) and multi-day jumps (e.g. the week arrows) have buffer to animate
 * into. The window recenters — invisibly, since the dates line up — only when
 * the visible window drifts near a rendered edge.
 */
export function useDayWindow(visibleStart: Date, visibleCount: number): { windowStart: Date; totalCount: number } {
  // One extra page each side, plus a small margin so a full `visibleCount` jump
  // still lands inside the no-recenter band (and thus animates).
  const page = visibleCount + GUARD;
  const totalCount = visibleCount + page * 2;
  const [windowStart, setWindowStart] = useState(() => addDays(startOfDay(visibleStart), -page));

  useEffect(() => {
    const vs = startOfDay(visibleStart).getTime();
    const offset = Math.round((vs - windowStart.getTime()) / DAY_MS);
    if (offset >= GUARD && offset <= totalCount - visibleCount - GUARD) return;
    // Transition (low-priority): a recenter unmounts + mounts ~page columns in
    // one render; doing that at urgent priority blocked the main thread and
    // visibly FROZE the in-flight pan spring for a beat. Time-slicing it keeps
    // the animation fed; the pan is recenter-agnostic (useDayPan shifts the
    // translate by the window delta whenever this commit lands).
    startTransition(() => setWindowStart(addDays(startOfDay(visibleStart), -page)));
  }, [visibleStart, windowStart, page, totalCount, visibleCount]);

  return { windowStart, totalCount };
}
